import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import cx from "classnames";

import { seedFlagPlatform, useFeatureFlag } from "services/Flags";
import { QueryClientProvider } from "@tanstack/react-query";
import { type CSSProperties, lazy, Suspense, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
  type LoaderFunction,
  Outlet,
  ScrollRestoration,
  useLoaderData,
  useMatch,
} from "react-router-dom";

import {
  getGetMultiPlatformThemeQueryKey,
  getMultiPlatformTheme,
} from "@/api/orval/multi-platform/multi-platform";
import {
  getCurrentPlatform,
  getGetCurrentPlatformQueryKey,
  getGetThemeQueryKey,
  getTheme,
} from "@/api/orval/platform/platform";
import { useScrollToTopOnRouteChange } from "@/hooks";
import { OpenMobileAppBanner } from "@/molecules/open-mobile-app-banner/OpenMobileAppBanner";
import {
  getAppsFlyerParams,
  getReferralCodeFromUrl,
  LINK_TYPES,
  openLinkToMobileApp,
} from "@/services/DeepLinkService";
import {
  getMultiPlatformPlatformByPlatformIdTheme,
  getPlatformCurrent,
  getPlatformTheme,
} from "api/methods";
import { queryClient } from "api/queryClient";
import { STALE_TIME } from "api/staleTimes";
import AppUrlListener from "appUrlListener";
import { CartContext, useCartState } from "contexts/CartContext";
import { ThemeContext } from "contexts/ThemeContext";
import { AnalyticsPageTracker } from "modules/analytics/AnalyticsPage/AnalyticsPage";
import { restoreBrowseLocationHeader } from "modules/anonymousBrowsing/anonymousLocation";
import {
  setAnonymousBrowsingAllowed,
  setAnonymousCheckoutAllowed,
} from "modules/anonymousBrowsing/anonymousBrowsingState";
import { AppVersionCheck } from "modules/appVersion";
import { ensureClientActivityConfigured } from "modules/clientActivity/appClientActivity";
import { DatadogRouteTracker } from "modules/datadog/DatadogRouteTracker";
import { EMAIL_PREFERENCES_PATH } from "modules/emailPreferences/emailPreferencesRoute";
import { useIsDesktopLayout } from "hooks/useIsDesktopLayout";
import { LINK_IN_BIO_PATH } from "modules/linkInBio/linkInBioRoute";
import { SAVE_CONTACT_PATH } from "modules/saveContact/saveContactRoute";
import Favicon from "pages/Favicon";
import { hexToRgb } from "toolbox/Colors";
import { isNativeMobileApp } from "utils/platform.helpers";

// The app client lives in api/queryClient and the theme context in contexts/ThemeContext; imports from here keep working.
export { queryClient, ThemeContext };

const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools/production").then(({ ReactQueryDevtools }) => ({
    default: ReactQueryDevtools,
  })),
);

/**
 * Render the React Query Devtools in development and (on demand) in production
 */
const ReactQueryDevTools = () => {
  const [showTools, setShowTools] = useState(
    // Show devtools only in development on the web by default
    import.meta.env.DEV && !isNativeMobileApp(),
  );

  useEffect(() => {
    // @ts-expect-error This function isn't called from the app, it's just for
    // debugging purposes in production
    globalThis.toggleReactQueryDevTools = () => setShowTools((prev) => !prev);
  }, []);

  if (!showTools) return null;

  return (
    <Suspense fallback={null}>
      <ReactQueryDevtools />
    </Suspense>
  );
};

const transformThemeToCss = (theme: { brandColor?: string | null; brandTint?: string | null }) => {
  return {
    "--repeatmd-brand-color": hexToRgb(theme.brandColor),
    "--repeatmd-brand-tint": hexToRgb(theme.brandTint ?? theme.brandColor, 0.1),
  } as CSSProperties;
};

const load = async () => {
  const native = isNativeMobileApp();

  const platformPromise = queryClient.fetchQuery({
    ...getPlatformCurrent.keyed(),
    queryFn: () => getPlatformCurrent().catch(() => null),
    staleTime: STALE_TIME.platformConfig,
  });
  // Only the native theme lookup takes a platform id; the web one is keyed by host, so it starts
  // alongside the platform request rather than behind it.
  const webThemePromise = native
    ? undefined
    : queryClient.fetchQuery({
        ...getPlatformTheme.keyed(),
        staleTime: STALE_TIME.platformConfig,
      });

  const platform = await platformPromise;
  setAnonymousBrowsingAllowed(platform?.platformSettings?.anonymousBrowsingEnabled === true);
  setAnonymousCheckoutAllowed(platform?.platformSettings?.anonymousCheckoutEnabled === true);
  restoreBrowseLocationHeader();

  const platformId = platform?.id;
  ensureClientActivityConfigured(platformId);
  if (platform) await seedFlagPlatform(platform);
  const mobileAppDiscovery = platform?.platformSettings?.mobileAppDiscovery;

  if (native && platformId) {
    const theme = await queryClient.fetchQuery({
      ...getMultiPlatformPlatformByPlatformIdTheme.keyed({ platformId }),
      staleTime: STALE_TIME.platformConfig,
    });

    return {
      theme: transformThemeToCss(theme),
      platformId,
    };
  }

  const [theme, bannerParams] = await Promise.all([
    webThemePromise ??
      queryClient.fetchQuery({
        ...getPlatformTheme.keyed(),
        staleTime: STALE_TIME.platformConfig,
      }),
    getAppsFlyerParams(location.href, LINK_TYPES.BANNER, platformId),
  ]);

  return {
    theme: transformThemeToCss(theme),
    platformId,
    mobileAppDiscovery,
    bannerParams,
  };
};

export const rootLoader: LoaderFunction = () => {
  return load();
};

const fetchBrandThemeCss = async (): Promise<CSSProperties> => {
  const native = isNativeMobileApp();
  // Same shape as the loader's lookup: a host with no practice behind it has no current platform.
  const platform = await queryClient.fetchQuery({
    queryKey: getGetCurrentPlatformQueryKey(),
    queryFn: () => getCurrentPlatform().catch(() => null),
    staleTime: 0,
  });
  const platformId = platform?.id;

  if (native && platformId) {
    const theme = await queryClient.fetchQuery({
      queryKey: getGetMultiPlatformThemeQueryKey(platformId),
      queryFn: () => getMultiPlatformTheme(platformId),
      staleTime: 0,
    });
    return transformThemeToCss(theme);
  }

  const theme = await queryClient.fetchQuery({
    queryKey: getGetThemeQueryKey(),
    queryFn: () => getTheme(),
    staleTime: 0,
  });
  return transformThemeToCss(theme);
};

type LoaderData = Awaited<ReturnType<typeof load>>;

const Root = () => {
  const { theme, platformId, mobileAppDiscovery, bannerParams } = useLoaderData() as LoaderData;
  const isDesktopExperience = useIsDesktopLayout();
  // Read on every page so a dev experiment on it can assign; nothing depends on the value.
  useFeatureFlag("patient-smoke-e2e");

  useScrollToTopOnRouteChange();

  // The save-contact page is only the practice's card: no install banner competing with Save contact.
  const isSaveContactPage = useMatch(SAVE_CONTACT_PATH) !== null;
  // The link-in-bio page has its own "Get the app" bar, so it is the only app prompt there.
  const isLinkInBioPage = useMatch(LINK_IN_BIO_PATH) !== null;
  // The banner hands the page's URL to AppsFlyer, and this page's URL carries the patient's token.
  const isEmailPreferencesPage = useMatch(EMAIL_PREFERENCES_PATH) !== null;
  const showSmartBanner =
    !isNativeMobileApp() &&
    !isSaveContactPage &&
    !isLinkInBioPage &&
    !isEmailPreferencesPage &&
    (mobileAppDiscovery || platformId === undefined);

  const cartState = useCartState();
  const referralCode = getReferralCodeFromUrl(location.href);
  const [refreshedTheme, setRefreshedTheme] = useState<CSSProperties | null>(null);
  const [loaderTheme, setLoaderTheme] = useState(theme);
  // Drops a color fetch that finishes after the router has already delivered a newer theme.
  const themeRequest = useRef(0);
  if (theme !== loaderTheme) {
    themeRequest.current += 1;
    setLoaderTheme(theme);
    setRefreshedTheme(null);
  }

  useEffect(() => {
    if (isNativeMobileApp()) {
      // App chrome is white even when the OS is in dark mode; without this, dark-mode
      // devices render light status bar icons on the white background.
      void StatusBar.setStyle({ style: Style.Light }).catch(() => undefined);
    }
    SplashScreen.hide();
  }, []);

  const resetTheme = () => {
    // A route revalidation also re-runs the sign-in gate, which sends a finished
    // profile home. Colors update on their own.
    const request = ++themeRequest.current;
    void fetchBrandThemeCss().then((next) => {
      if (themeRequest.current === request) setRefreshedTheme(next);
    });
  };

  const renderAppsFlyerSmartBanner = () => {
    return (
      <Helmet>
        <script>
          {`
          !function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){(t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","banners",{banners: {key: "0c9a84e8-2164-44d0-acfb-9d75834d8547"}});
          AF('banners', 'showBanner', ${JSON.stringify({
            additionalParams: bannerParams,
          })})
          `}
        </script>
      </Helmet>
    );
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AnalyticsPageTracker />
      <DatadogRouteTracker />
      <AppVersionCheck />
      <AppUrlListener resetTheme={resetTheme} />
      <>
        {showSmartBanner && renderAppsFlyerSmartBanner()}
        {showSmartBanner && (
          <OpenMobileAppBanner onClick={() => openLinkToMobileApp(platformId, referralCode)} />
        )}
        <Favicon />
        <CartContext.Provider value={cartState}>
          {/* The app container. With the desktop experience on it becomes a
              query container, which is what every `desk:*` style resolves
              against — so the layout follows the width the app actually has.
              With the flag off there is no container at all, so none of those
              styles can match and the phone layout holds at any window size. */}
          <div
            style={refreshedTheme ?? theme}
            className={cx(
              "mx-auto w-full",
              isDesktopExperience
                ? "[container-name:app] [container-type:inline-size]"
                : "sm:max-w-[390px]",
            )}
          >
            <ThemeContext.Provider value={{ resetTheme }}>
              <Outlet />
            </ThemeContext.Provider>
          </div>
          <ScrollRestoration />
        </CartContext.Provider>
      </>
      <ReactQueryDevTools />
    </QueryClientProvider>
  );
};

export default Root;
